"use client";

import { useRef, useState } from "react";
import { useMotionMode } from "@/context/MotionContext";
import { PlayIcon } from "./Button";

// =============================================================================
// VIDEO PLAYER
// =============================================================================
// Motion-mode-aware <video> with play-button fallback and error state.
//
// Behavior:
//   - motionMode === "on"  : autoplay + loop + muted (when autoPlayWhenMotionOn)
//   - motionMode === "off" : poster frame + center play icon, click to play
//   - onError              : caller-provided hook (e.g. swap to poster <img>)
//
// Perf notes:
//   - preload defaults to "none" so the underlying video file does not download
//     until the user clicks play (or the parent upgrades preload in expanded mode).
//     This is the main reason the blog masonry can hold dozens of pet videos
//     without cooking laptops.
//   - controlsList + disablePicture-in-picture + disableRemotePlayback keep the
//     browser from offering download/cast affordances we don't want.

export interface VideoPlayerProps {
  src: string;
  /** Poster frame shown before load / when motion is off */
  poster?: string;
  /** Alt text for accessibility (read by screen readers) */
  alt?: string;
  /** Optional caption used as aria-label fallback */
  caption?: string;
  /** Extra classes for the inner <video> element */
  className?: string;
  /** When true (default), video autoplays if motionMode === "on" */
  autoPlayWhenMotionOn?: boolean;
  /** When true, shows native controls (used for prototype walkthroughs) */
  controls?: boolean;
  /** Explicit muted override. Default: muted when controls=false, unmuted when controls=true.
   *  Pass muted={false} to play audio without native controls (e.g. blog gig videos). */
  muted?: boolean;
  /** Preload strategy -- default "none" for perf, bump to "metadata" or "auto" when expanded */
  preload?: "none" | "metadata" | "auto";
  /** Called when the <video> element fails to load */
  onError?: () => void;
}

export function VideoPlayer({
  src,
  poster,
  alt,
  caption,
  className,
  autoPlayWhenMotionOn = true,
  controls = false,
  muted: mutedProp,
  preload = "none",
  onError,
}: VideoPlayerProps) {
  const { mode: motionMode } = useMotionMode();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(false);

  // Explicit muted prop wins; otherwise: muted when no native controls
  const isMuted = mutedProp !== undefined ? mutedProp : !controls;

  const shouldAutoPlay =
    autoPlayWhenMotionOn && !controls && (motionMode === "on" || playing);

  return (
    <div className="relative">
      <video
        ref={videoRef}
        key={src}
        src={src}
        poster={poster}
        autoPlay={shouldAutoPlay}
        loop
        muted={isMuted}
        controls={controls}
        controlsList="nodownload noplaybackrate"
        playsInline
        preload={preload}
        disablePictureInPicture
        disableRemotePlayback
        aria-label={alt || caption || "Video content"}
        className={className}
        onPlay={() => setPlaying(true)}
        onPause={() => !controls && setPlaying(false)}
        onError={onError}
      />

      {/* Play-button overlay -- only when motion is off and we aren't already playing.
          Clicking it kicks off playback and hides the overlay. */}
      {motionMode === "off" && !controls && !playing && (
        <button
          type="button"
          className="absolute inset-0 flex items-center justify-center bg-black/30 cursor-pointer transition-opacity hover:bg-black/40 rounded-[inherit]"
          onClick={(e) => {
            e.stopPropagation();
            videoRef.current?.play();
            setPlaying(true);
          }}
          aria-label="Play video"
        >
          <PlayIcon size={56} className="text-white drop-shadow-lg" />
        </button>
      )}
    </div>
  );
}
